import WebGLObject from './WebGLObject';
import TexturesController from './TexturesController';
import { PLANE_GEOMETRY, BOX_GEOMETRY, ROUNDED_RECT_GEOMETRY } from '../../../3D/constants';
import { FrontSide, ShaderMaterial } from 'three';
import CanvasColorTexture from './CanvasColorTexture';
import { IMAGE_FRAGMENT } from '../shaders/image-fragment';
import { IMAGE_VERTEX } from '../shaders/image-vertex';
import { DoubleSide, VideoTexture } from 'three/src/Three.js';
import { GetBy } from '../core/Element';
import { isSmartphone } from '../core/Basics';
import { Metrics } from '../core/Metrics';
import gsap from 'gsap';


export default class Image extends WebGLObject {
    texture;
    isImageLoaded = false;
    isVideoLoaded = false;
    isRunning = false;
    video = null;

    constructor(opts = {}) {
        super(opts);

        if (!this.geometry) {
            this.geometry = ROUNDED_RECT_GEOMETRY;
        }

        if (!this.material) {
            this.setupMaterial();
        }

        this.video = GetBy.tag('video', this.dom)[0];
        this.init();
    }

    init() {
        super.init();
        this.loadTexture();
        this.resize();
    }

    pause() {
        if(this.video && this.isRunning && !isSmartphone) this.video.pause();
        this.isRunning = false;
    }

    play() {
        if(this.video && !this.isRunning && !isSmartphone) this.video.play();
        this.isRunning = true;
    }

    setupMaterial() {
        this.material = new ShaderMaterial({
            uniforms: {
                texture1: { type: 't', value: new CanvasColorTexture().texture },
                mask: { type: 't', value: new CanvasColorTexture().texture },
                textureOverlay: { type: 't', value: new CanvasColorTexture('#000000').texture },
                opacity: { type: 'f', value: 1 },
                imageScale: { type: 'f', value: 1 },
                darken: { type: 'f', value: 0 },
                cylinderEffect: { type: 'f', value: 0 },
                direction: { type: 'f', value: 0 },
                scaleCenter: { type: 'f', value: 0 },
                resolution: {
                    type: 'v2',
                    value: { x: 1, y: 1 }
                },
                mouse: {
                    type: 'v2',
                    value: { x: 0, y: 0 }
                }
            },
            fragmentShader: IMAGE_FRAGMENT,
            vertexShader: IMAGE_VERTEX,
            transparent: false,
            side: DoubleSide
        });
    }

    loadTexture() {
        if (this.dom.dataset.src) {
            TexturesController.load({
                src: this.dom.dataset.src,
                call: (texture)=> {
                    this.isImageLoaded = true;   
                    
                    if(!this.isVideoLoaded) {
                        this.material.uniforms.texture1.value = texture;
                    }
                } 
            });
        }

        if (this.video && !isSmartphone) {
            this.video.crossOrigin = 'anonymous';

            const _onCanPlayThrough = () => {
                if (this.video.readyState >= this.video.HAVE_ENOUGH_DATA) {
                    this.video.removeEventListener('canplaythrough', _onCanPlayThrough);
                    this.isVideoLoaded = true;
                     this.video.currentTime = 0;
                    this.material.uniforms.textureOverlay.value = new VideoTexture(this.video);
                    gsap.to(this.material.uniforms.darken,{value:1, time:.02, delay:.02})
                }
            };

            this.video.addEventListener('canplaythrough', _onCanPlayThrough);
        }

        if (this.dom.dataset.mask) {
            TexturesController.load({
                src: this.dom.dataset.mask,
                material: this.material.uniforms.texture1,
                attribute: "value"
            });
        }
    }

    calculateResolution() {
        const width = this.size.x;
        const height = this.size.y;

        const planeAspect = this.mesh.scale.y / this.mesh.scale.x;
        let a1;
        let a2;

        if (height / width > planeAspect) {
            const h = (width / height) * planeAspect;
            // Canvas more horizontal than image
            a1 = 1;
            a2 = h;
        } else {
            // Canvas more vertical than image
            a1 = height / width / planeAspect;
            a2 = 1;
        }

        return {
            x: a1,
            y: a2
        };
    }

    resize() {
        const { x, y, width, height } = this.dom.getBoundingClientRect();
        
        if(!this.opts.hasFreePosition) {
            const position = this.domPositionTo3D(x, y);
            this.position.x =
                this.pos.x = position.x + width * .5;
            this.position.y =
                this.pos.y = position.y - height * .5;
        }

        
        super.resize(width/400, height/300);

        this.material.uniforms.resolution.value = this.calculateResolution();
    }
}
